import { createContext, useContext } from 'react'
import { Trans } from 'react-i18next'
import uniqueId from 'lodash/uniqueId'

import { TypographySelectValue, TypographyTextTransform } from '@makyoapp/makyo-common'
import { getTypographyTokenStyle } from '@makyoapp/makyo-tw-ui'
import { Field, SelectDropdown, Slider } from '@makyoapp/makyo-ui'

import { MT_SIZE_OPTIONS, MT_TYPE_TYPE, SizeOption } from '@/shared/brand-kit'
import { dashifyString } from '@/shared/utils'

import { FontStyleSelect } from '../FontSelect/FontSelect'
import { FontSelectOptimized } from '../FontSelect/FontSelectOptimized'

export interface TypographySelectContextProps {
  id: string
  value: TypographySelectValue
  onChange: (nextValue: TypographySelectValue) => void
}

const TypographySelectContext = createContext<TypographySelectContextProps>({
  id: '',
  value: {
    typography: 'Body Medium',
  },
  onChange: () => null,
})

const DEFAULT_VALUE = {
  fontFamily: 'Roboto',
  fontStyle: '400',
  fontSize: 16,
  lineHeight: 1,
  letterSpacing: 0,
  textTransform: TypographyTextTransform.None,
}

interface FormattedOption {
  label: string
  value: string
}

const EXCLUDED_TYPES = ['HTML', 'Navigation Bar']

function TextTransformField() {
  const { id, value, onChange } = useContext(TypographySelectContext)
  return value.typography === 'Custom' ? (
    <Field isVertical>
      <label>
        <Trans>Text Transform</Trans>
      </label>
      <SelectDropdown
        id={`${id}-text-transform`}
        onChange={(textTransform) => {
          onChange({
            ...value,
            custom: {
              ...(value.custom ?? DEFAULT_VALUE),
              textTransform,
            },
          })
        }}
        value={value.custom?.textTransform}
        options={Object.values(TypographyTextTransform)}
      />
    </Field>
  ) : null
}

function LetterSpacingField() {
  const { id, value, onChange } = useContext(TypographySelectContext)
  return value.typography === 'Custom' ? (
    <Field isVertical>
      <label>
        <Trans>Letter Spacing</Trans>
      </label>
      <Slider
        max={5}
        min={-5}
        step={0.05}
        id={`${id}-letter-spacing`}
        renderLabel={(v) => `${v}px`}
        renderTrackLabel={(v) => `${v}px`}
        values={[value.custom?.letterSpacing ?? DEFAULT_VALUE.letterSpacing]}
        onChange={([letterSpacing]) => {
          onChange({
            ...value,
            custom: {
              ...(value.custom ?? DEFAULT_VALUE),
              letterSpacing,
            },
          })
        }}
      />
    </Field>
  ) : null
}

function LineHeightField() {
  const { id, value, onChange } = useContext(TypographySelectContext)
  return value.typography === 'Custom' ? (
    <Field isVertical>
      <label>
        <Trans>Line Height</Trans>
      </label>
      <Slider
        max={10}
        min={0}
        step={0.1}
        id={`${id}-line-height`}
        values={[value.custom?.lineHeight ?? DEFAULT_VALUE.lineHeight]}
        onChange={([lineHeight]) => {
          onChange({
            ...value,
            custom: {
              ...(value.custom ?? DEFAULT_VALUE),
              lineHeight,
            },
          })
        }}
      />
    </Field>
  ) : null
}

function FontSizeField() {
  const { id, value, onChange } = useContext(TypographySelectContext)
  return value.typography === 'Custom' ? (
    <Field isVertical>
      <label>
        <Trans>Font Size</Trans>
      </label>
      <Slider
        max={100}
        min={8}
        step={1}
        id={`${id}-font-size`}
        values={[value.custom?.fontSize ?? DEFAULT_VALUE.fontSize]}
        onChange={([fontSize]) => {
          onChange({
            ...value,
            custom: {
              ...(value.custom ?? DEFAULT_VALUE),
              fontSize,
            },
          })
        }}
      />
    </Field>
  ) : null
}

function FontStyleField() {
  const { id, value, onChange } = useContext(TypographySelectContext)
  return value.typography === 'Custom' ? (
    <Field isVertical>
      <label>
        <Trans>Font Style</Trans>
      </label>
      <FontStyleSelect
        id={`${id}-font-style`}
        onChange={(fontStyle) => {
          onChange({
            ...value,
            custom: {
              ...(value.custom ?? DEFAULT_VALUE),
              fontStyle,
            },
          })
        }}
        value={value.custom?.fontStyle}
        fontFamily={value.custom?.fontFamily ?? DEFAULT_VALUE.fontFamily}
      />
    </Field>
  ) : null
}

function FontFamilyField() {
  const { id, value, onChange } = useContext(TypographySelectContext)
  return value.typography === 'Custom' ? (
    <Field isVertical>
      <label>
        <Trans>Font Family</Trans>
      </label>
      <FontSelectOptimized
        id={`${id}-font-family`}
        value={value.custom?.fontFamily}
        onChange={(fontFamily) => {
          const needReset = fontFamily !== value.custom?.fontFamily
          const nextValReset = needReset
            ? {
                fontStyle: '400',
              }
            : {}

          onChange({
            ...value,
            custom: {
              ...(value.custom ?? DEFAULT_VALUE),
              fontFamily,
              ...nextValReset,
            },
          })
        }}
      />
    </Field>
  ) : null
}

function formatTypographyOptions(types: string[], sizes: SizeOption[]): FormattedOption[] {
  const filteredTypes = types.filter((type) => !EXCLUDED_TYPES.includes(type))

  return filteredTypes.flatMap((type) =>
    sizes.map((size) => ({
      label: `${type} ${size.abr} (${size.html_tag})`,
      value: `${type} ${size.label}`,
    }))
  )
}

const selectOptions = [...formatTypographyOptions(MT_TYPE_TYPE, MT_SIZE_OPTIONS), { label: 'Custom', value: 'Custom' }]

const toKebabCase = (value: string = ''): string => {
  return value?.toLowerCase().replace(/\s+/g, '-')
}

function TypographyField() {
  const { id, onChange, value } = useContext(TypographySelectContext)

  const fontOptions = [
    ...selectOptions.map((opt) => {
      if (opt.value === 'Custom') return opt.value
      return dashifyString(toKebabCase(opt.value))
    }),
  ]

  const HTML_TAGS = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']

  return (
    <Field isVertical>
      <label>
        <Trans>Variant</Trans>
      </label>
      <SelectDropdown
        id={`${id}-typography`}
        enableAutoScroll
        onChange={(opt) => {
          const next = { ...value, typography: opt }
          if (opt === 'Custom') {
            next.custom = DEFAULT_VALUE
          } else {
            delete next.custom
          }
          onChange(next)
        }}
        value={dashifyString(toKebabCase(value.typography))}
        renderOption={(opt) => {
          const typography = dashifyString(toKebabCase(opt))

          const option =
            selectOptions.find((o) => dashifyString(toKebabCase(o.value)) === typography)?.label.split(' (') ?? []

          return <div style={getTypographyTokenStyle({ typography })}>{option[0]}</div>
        }}
        options={fontOptions}
      />
    </Field>
  )
}

export interface TypographySelectProps {
  id?: string
  value?: TypographySelectValue
  onChange: (nextValue: TypographySelectValue) => void
}

export function TypographySelect({
  id = uniqueId('ts-'),
  value = {
    typography: 'Body Medium',
  },
  onChange,
}: TypographySelectProps) {
  return (
    <TypographySelectContext.Provider value={{ id, value, onChange }}>
      <TypographyField />
      <FontFamilyField />
      <FontStyleField />
      <FontSizeField />
      <LineHeightField />
      <LetterSpacingField />
      <TextTransformField />
    </TypographySelectContext.Provider>
  )
}
